// V3 Loyalty Console — programs/accounts/ledger/rewards/redemptions
// esclusivamente su /api/v2 (cap_loyalty). Prima UI di una capability
// veramente trasversale: la stessa pagina serve hair/beauty/gym/
// restaurant senza sapere il verticale.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });

export function V3LoyaltyPage() {
  const [programs, setPrograms] = useState<Row[]>([]);
  const [tiers, setTiers] = useState<Row[]>([]);
  const [accounts, setAccounts] = useState<Row[]>([]);
  const [transactions, setTransactions] = useState<Row[]>([]);
  const [rewards, setRewards] = useState<Row[]>([]);
  const [redemptions, setRedemptions] = useState<Row[]>([]);
  const [customers, setCustomers] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [earnForm, setEarnForm] = useState({ account_id: '', points: '', note: '' });
  const [redeemForm, setRedeemForm] = useState({ account_id: '', reward_id: '' });

  const load = useCallback(async () => {
    try {
      const [p, t, a, tx, r, rd, c] = await Promise.all([
        apiFetch('/api/v2/loyalty-programs'), apiFetch('/api/v2/loyalty-tiers'),
        apiFetch('/api/v2/loyalty-accounts'), apiFetch('/api/v2/loyalty-transactions'),
        apiFetch('/api/v2/loyalty-rewards'), apiFetch('/api/v2/loyalty-redemptions'),
        apiFetch('/api/v2/customers'),
      ]);
      setPrograms(await p.json()); setTiers(await t.json());
      setAccounts(await a.json()); setTransactions(await tx.json());
      setRewards(await r.json()); setRedemptions(await rd.json());
      setCustomers(await c.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const totalBalance = useMemo(
    () => accounts.reduce((s, a) => s + Number(a.balance || 0), 0),
    [accounts],
  );

  const earn = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/loyalty-transactions', {
        method: 'POST',
        body: JSON.stringify({
          account_id: Number(earnForm.account_id),
          type: Number(earnForm.points) >= 0 ? 'earn' : 'adjustment',
          points: Number(earnForm.points),
          note: earnForm.note || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setEarnForm({ account_id: '', points: '', note: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore transazione');
    } finally {
      setSaving(false);
    }
  };

  const redeem = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/loyalty-redemptions', {
        method: 'POST',
        body: JSON.stringify({
          account_id: Number(redeemForm.account_id),
          reward_id: Number(redeemForm.reward_id),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setRedeemForm({ account_id: '', reward_id: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore riscatto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Accredito / storno punti</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={earnForm.account_id}
              onChange={(e) => setEarnForm({ ...earnForm, account_id: e.target.value })}>
              <option value="">Account cliente…</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.customer_name || `#${a.customer_profile_id}`} · {a.program_name} · saldo {a.balance}</option>)}
            </select>
            <input style={input} type="number" placeholder="Punti (+ earn / − storno)" value={earnForm.points}
              onChange={(e) => setEarnForm({ ...earnForm, points: e.target.value })} />
            <input style={input} placeholder="Causale" value={earnForm.note}
              onChange={(e) => setEarnForm({ ...earnForm, note: e.target.value })} />
            <button style={btn()} disabled={saving || !earnForm.account_id || !earnForm.points} onClick={() => void earn()}>
              {saving ? 'Salvataggio…' : 'Registra movimento'}
            </button>
          </div>
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Riscatta premio</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={redeemForm.account_id}
              onChange={(e) => setRedeemForm({ ...redeemForm, account_id: e.target.value })}>
              <option value="">Account cliente…</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.customer_name || `#${a.customer_profile_id}`} · saldo {a.balance}</option>)}
            </select>
            <select style={input} value={redeemForm.reward_id}
              onChange={(e) => setRedeemForm({ ...redeemForm, reward_id: e.target.value })}>
              <option value="">Premio…</option>
              {rewards.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.cost_points} pt</option>)}
            </select>
            <button style={btn('#0d6efd')} disabled={saving || !redeemForm.account_id || !redeemForm.reward_id} onClick={() => void redeem()}>
              {saving ? 'Salvataggio…' : 'Emetti riscatto'}
            </button>
          </div>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2 · {programs.length} programmi · {accounts.length} account · saldo totale {totalBalance}
        </p>
      </div>

      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Programmi fedeltà</h4>
          {programs.length === 0 && <p className="sk-muted">Nessun programma.</p>}
          {programs.map((p) => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{p.name}</strong></span>
              <span style={{ color: '#6b7280' }}>
                {p.attrs?.points_per_euro ? `${p.attrs.points_per_euro} pt/€` : ''}
                {p.attrs?.points_per_visit ? ` · ${p.attrs.points_per_visit} pt/visita` : ''}
                {' · '}{p.is_active ? 'attivo' : 'off'}
              </span>
            </div>
          ))}
          {tiers.length > 0 && (
            <p className="sk-muted" style={{ fontSize: 12, marginTop: 8 }}>
              Livelli: {tiers.map((t) => `${t.name} (${t.min_points}pt)`).join(' · ')}
            </p>
          )}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Account punti ({accounts.length})</h4>
          {accounts.length === 0 && <p className="sk-muted">Nessun account.</p>}
          {accounts.slice(0, 20).map((a) => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{a.customer_name || `#${a.customer_profile_id}`}</strong> · {a.program_name}</span>
              <span style={{ color: '#6b7280' }}>{a.tier_name ? `${a.tier_name} · ` : ''}{a.balance} pt</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Ledger movimenti</h4>
          {transactions.length === 0 && <p className="sk-muted">Nessun movimento.</p>}
          {transactions.slice(0, 20).map((t) => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span>{t.type} · <strong>{t.points}</strong></span>
              <span style={{ color: '#6b7280' }}>{t.note || ''} · {String(t.created_at).slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Riscatti emessi ({redemptions.length})</h4>
          {redemptions.length === 0 && <p className="sk-muted">Nessun riscatto.</p>}
          {redemptions.slice(0, 15).map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{r.attrs?.reward_name || r.code || `#${r.id}`}</strong> · {r.points_spent} pt</span>
              <span style={{ color: '#6b7280' }}>{r.status} · {String(r.created_at).slice(0, 10)}</span>
            </div>
          ))}
        </div>

        <p className="sk-muted" style={{ fontSize: 12 }}>{customers.length} clienti nel profilo customer core.</p>
      </div>
    </div>
  );
}
